import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Mail, Lock, User as UserIcon, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import Header from '../components/Header';

const AuthPage = ({ mode = 'login' }) => {
  const isRegister = mode === 'register';
  const { login, register, startGoogleLogin } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = new URLSearchParams(location.search).get('next') || '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (isRegister) {
        await register(email, password, name);
      } else {
        await login(email, password);
      }
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || t('somethingWentWrong'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0e10] text-white">
      <Header />
      <main className="px-4 py-10">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8 fade-in-up">
            <h1 className="heading-font text-4xl font-black tracking-wide">
              {isRegister ? t('createAccount') : t('welcomeBack')}
            </h1>
            <p className="mt-2 text-white/70 text-sm">
              {isRegister ? t('saveYourPlan') : t('signInToView')}
            </p>
          </div>

          <button
            onClick={startGoogleLogin}
            className="w-full flex items-center justify-center gap-3 py-3 bg-white text-[#0d0e10] rounded-sm font-semibold hover:bg-white/90 transition-colors"
            data-testid="google-signin-btn"
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {t('continueWithGoogle')}
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-[#2a2b2f]" />
            <span className="text-white/40 text-xs uppercase tracking-widest">{t('or')}</span>
            <div className="flex-1 h-px bg-[#2a2b2f]" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {isRegister && (
              <div className="relative">
                <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('namePlaceholder')}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-[#1a1b1e] border border-[#2a2b2f] focus:border-[#ff4e15] outline-none rounded-sm text-white transition-colors"
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('emailPlaceholder')}
                required
                className="w-full pl-10 pr-4 py-3 bg-[#1a1b1e] border border-[#2a2b2f] focus:border-[#ff4e15] outline-none rounded-sm text-white transition-colors"
                data-testid="auth-email-input"
              />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('passwordPlaceholder')}
                required
                minLength={6}
                className="w-full pl-10 pr-4 py-3 bg-[#1a1b1e] border border-[#2a2b2f] focus:border-[#ff4e15] outline-none rounded-sm text-white transition-colors"
                data-testid="auth-password-input"
              />
            </div>

            {!isRegister && (
              <div className="text-right -mt-2">
                <Link
                  to="/forgot-password"
                  className="text-xs text-white/60 hover:text-[#ff4e15] transition-colors"
                  data-testid="forgot-password-link"
                >
                  {t('forgotPassword')}
                </Link>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 text-sm text-[#ff4e15] bg-[#1f1210] border border-[#ff4e15]/40 px-3 py-2 rounded-sm">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full py-3.5 rounded-sm font-bold uppercase tracking-wider text-white bg-[#ff4e15] hover:bg-[#ff6033] disabled:opacity-60 transition-colors"
              data-testid="auth-submit-btn"
            >
              {busy ? t('pleaseWait') : isRegister ? t('createAccountBtn') : t('signInBtn')}
            </button>
          </form>

          <p className="text-center text-sm text-white/60 mt-6">
            {isRegister ? (
              <>{t('alreadyHaveAccount')} <Link to="/login" className="text-[#ff4e15] hover:underline">{t('signInBtn')}</Link></>
            ) : (
              <>{t('newHere')} <Link to="/register" className="text-[#ff4e15] hover:underline">{t('createAccountBtn')}</Link></>
            )}
          </p>
        </div>
      </main>
    </div>
  );
};

export default AuthPage;
